import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Loader2, Trophy } from "lucide-react";
import { royai, fmtUsd } from "@/lib/royai";
import LcdDisplay from "@/components/LcdDisplay";

/* Catalog Summary — bottom of the RoyAI page.
 * Rolls up total streams, total revenue, and total user share across every
 * audited track this user has, and lets them "Finalize & Add to Portfolio"
 * which persists a portfolio_finalized flag and makes the tracks show in
 * Section 08 on the dashboard. */
export default function CatalogSummary() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);

  const load = async () => {
    try {
      const data = await royai.catalogSummary();
      setSummary(data);
    } catch (e) {
      // silent — this section is optional
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const finalize = async () => {
    setFinalizing(true);
    try {
      const res = await royai.finalizePortfolio();
      toast.success(
        res.newly_finalized > 0
          ? `Added ${res.newly_finalized} track${res.newly_finalized === 1 ? "" : "s"} to your Portfolio`
          : "Portfolio already up to date",
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finalize portfolio");
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) return null;
  if (!summary || summary.track_count === 0) return null;

  const allFinalized = summary.finalized_count >= summary.track_count;

  return (
    <section
      className="mt-10 bg-gradient-to-br from-[#0c0c0c] via-[#0a0a0a] to-[#050505] border border-amber-500/20 p-5 lg:p-7"
      data-testid="royai-catalog-summary"
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-overline text-amber-500 mb-1">Catalog Summary</div>
          <h3 className="font-display text-lg lg:text-xl font-medium tracking-tight text-white">
            Everything you&apos;ve audited, in one totalizer.
          </h3>
        </div>
        <span className="text-xs text-neutral-500 font-mono-tech">
          {summary.track_count} tracks · {summary.platform_rows} platform rows
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <SummaryTile
          label="Total Plays"
          value={summary.total_plays}
          hint={`${summary.total_streams.toLocaleString()} streams · ${summary.total_ugc_views.toLocaleString()} UGC views`}
          testid="catalog-total-plays"
        />
        <SummaryTile
          label="Total Revenue Audited"
          value={Math.floor(summary.total_revenue_usd)}
          prefix="$"
          hint="Sum across every platform row"
          testid="catalog-total-revenue"
        />
        <SummaryTile
          label="Your Share (Recoverable)"
          value={Math.floor(summary.your_share_usd)}
          prefix="$"
          hint="Weighted by ownership + leak multiplier"
          testid="catalog-your-share"
          gold
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-white/5">
        <div className="flex items-center gap-2">
          <Trophy size={16} className="text-amber-500" />
          <div className="text-xs text-neutral-300">
            {allFinalized
              ? `All ${summary.track_count} tracks locked into your Portfolio.`
              : `${summary.finalized_count} of ${summary.track_count} finalized — freeze this audit into your permanent Portfolio.`}
          </div>
        </div>
        <button
          onClick={finalize}
          disabled={finalizing}
          data-testid="finalize-portfolio-btn"
          className="inline-flex items-center gap-2 bg-amber-500 text-black text-sm font-semibold px-5 py-2.5 hover:brightness-110 transition-all disabled:opacity-50"
        >
          {finalizing ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Finalizing…
            </>
          ) : allFinalized ? (
            <>
              <CheckCircle2 size={14} /> Refresh Portfolio
            </>
          ) : (
            <>
              Finalize &amp; Add to Portfolio <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </section>
  );
}

function SummaryTile({ label, value, prefix, hint, testid, gold = false }) {
  const s = String(Math.abs(Math.floor(value || 0)));
  const digits = Math.max(s.length, 4);
  return (
    <div
      className="bg-[#050505] border border-white/5 p-4 flex flex-col gap-2"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="flex items-baseline gap-1.5">
        {prefix && (
          <span
            className={`font-display text-base lg:text-lg ${gold ? "text-amber-500" : "text-white"}`}
          >
            {prefix}
          </span>
        )}
        <LcdDisplay value={Math.floor(value || 0)} digits={digits} size="sm" />
      </div>
      {hint && (
        <div className="text-[10px] text-neutral-500 font-mono-tech truncate" title={fmtUsd(value)}>
          {hint}
        </div>
      )}
    </div>
  );
}
